import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { NavigationStore } from '../navigation/navigation.store';
import { routeExists } from '../navigation/route-exists';

/**
 * Landing para el índice de `/app`.
 *
 * Antes había un mapa `ROLE_HOME` hardcodeado (rol → ruta). M21 lo elimina: la
 * misma clase de dato que esta HU movió al backend no puede seguir duplicada acá,
 * donde se desincroniza sola. Ahora aterriza en el primer ítem navegable del árbol,
 * que el servidor ya devuelve ordenado. Reproduce el comportamiento anterior sin el
 * mapa: el seed pone `tracking` primero y `station-portal` justo después, así que un
 * STATION_BASIC —sin `tracking.read`— sigue cayendo en su portal.
 *
 * `sections: []` es la respuesta normal de un usuario de la app móvil que entra al
 * portal web (§4.7 del contrato), no un error: va a /not-authorized, cuyo copy ya
 * dice exactamente eso y ofrece cerrar sesión.
 */
export const appHomeGuard: CanActivateFn = async () => {
  const nav = inject(NavigationStore);
  const router = inject(Router);

  await nav.ensureLoaded();

  if (nav.status() !== 'ready') return router.createUrlTree(['/navigation-unavailable']);

  // Profundidad 2 (garantizada por el contrato, ver `NavChildItem`): un ítem con
  // ruta propia se cuenta a sí mismo; un grupo puro (`route: null`) aporta sus
  // hijos en su lugar. Un hijo NUNCA tiene hijos, así que un solo nivel extra de
  // flatMap alcanza sin recursión.
  //
  // `routeExists` es la misma validación que aplica el menú (§4.2): el backend
  // puede sembrar un módulo cuyo screen todavía no se desplegó en este build. Sin
  // este chequeo, aterrizar en ese ítem manda al usuario derecho al wildcard
  // (`/not-found`) desde su propio home.
  const home = nav
    .sections()
    .flatMap((s) => s.items)
    .flatMap((item) => (item.route !== null ? [item] : item.children))
    .find(
      (i): i is typeof i & { route: string } =>
        i.route !== null && !i.disabled && routeExists(i.route, router.config),
    );

  return router.createUrlTree(home ? [home.route] : ['/not-authorized']);
};
